import { useEffect, useRef, useState } from "react";

// Bausteine des Admin-Designs (weiße Karten, Messing-Eyebrows, eckige Buttons).

export const PageTitle = ({ children, aside }) => (
    <div className="mb-4 flex items-baseline justify-between gap-4">
        <h1 className="m-0 font-caslon text-[28px] font-normal">{children}</h1>
        {aside && <span className="text-[13px] text-forest/55">{aside}</span>}
    </div>
);

export const Card = ({ children, className = "", ...props }) => (
    <div className={`border border-forest/10 bg-white p-4 ${className}`} {...props}>
        {children}
    </div>
);

export const Eyebrow = ({ children, className = "" }) => (
    <div className={`text-xs font-semibold uppercase tracking-[.2em] text-messing ${className}`}>{children}</div>
);

export const Hint = ({ children, className = "" }) => (
    <div className={`text-[13px] leading-normal text-forest/55 ${className}`}>{children}</div>
);

export const Empty = ({ children }) => <div className="py-8 text-[15px] text-forest/55">{children}</div>;

// Auswahl wie Filter oder Menü-Umschalter: aktive Option dunkel gefüllt.
export const ChoiceButtons = ({ options, value, onChange, label }) => (
    <div role="group" aria-label={label} className="mb-4 flex flex-wrap gap-2">
        {options.map(([id, text]) => (
            <button
                key={id}
                type="button"
                aria-pressed={value === id}
                onClick={() => onChange(id)}
                className={`min-h-[40px] border border-forest px-3.5 py-2 text-[13px] font-medium ${
                    value === id ? "bg-forest text-creme" : "bg-transparent text-forest"
                }`}
            >
                {text}
            </button>
        ))}
    </div>
);

export const AdminField = ({ label, as = "input", className = "", children, ...props }) => {
    const Tag = as;
    return (
        <label className={`grid gap-1.5 text-xs uppercase tracking-[.12em] text-forest/60 ${className}`}>
            {label}
            <Tag className="admin-input" {...props}>
                {children}
            </Tag>
        </label>
    );
};

export const Toggle = ({ label, description, checked, onChange, disabled }) => (
    <label className="flex min-h-[52px] items-center justify-between gap-4 border-b border-forest/10 py-2 last:border-b-0">
        <span>
            <span className="block text-base">{label}</span>
            {description && <span className="text-[13px] text-forest/55">{description}</span>}
        </span>
        <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
            className="h-[26px] w-[26px] flex-none accent-forest"
        />
    </label>
);

export const PrimaryButton = ({ children, className = "", ...props }) => (
    <button
        className={`min-h-[44px] bg-forest px-4 py-3.5 text-sm font-semibold uppercase tracking-[.04em] text-creme disabled:opacity-40 ${className}`}
        {...props}
    >
        {children}
    </button>
);

export const SecondaryButton = ({ children, className = "", ...props }) => (
    <button
        type="button"
        className={`min-h-[44px] border border-forest bg-transparent px-4 py-2.5 text-sm font-medium disabled:opacity-40 ${className}`}
        {...props}
    >
        {children}
    </button>
);

// Löschen in zwei Schritten: erster Klick fragt nach, zweiter löscht. Nach 4 s zurück.
export const ConfirmButton = ({ children, confirmLabel = "Wirklich löschen?", onConfirm, className = "" }) => {
    const [armed, setArmed] = useState(false);
    const [busy, setBusy] = useState(false);
    const timer = useRef(null);

    useEffect(() => () => clearTimeout(timer.current), []);

    const click = async () => {
        if (!armed) {
            setArmed(true);
            timer.current = setTimeout(() => setArmed(false), 4000);
            return;
        }
        clearTimeout(timer.current);
        setBusy(true);
        try {
            await onConfirm();
        } finally {
            setBusy(false);
            setArmed(false);
        }
    };

    return (
        <button
            type="button"
            onClick={click}
            disabled={busy}
            className={`min-h-[40px] border px-3.5 py-2 text-[13px] ${
                armed ? "border-fehler bg-fehler text-creme" : "border-fehler bg-transparent text-fehler"
            } ${className}`}
        >
            {armed ? confirmLabel : children}
        </button>
    );
};

// Rückmeldung nach dem Speichern (Erfolg grün, Fehler rot).
export const Feedback = ({ message }) =>
    message ? (
        <div role="status" className={`text-sm ${message.error ? "text-fehler" : "text-erfolg"}`}>
            {message.text}
        </div>
    ) : null;
